import * as React from 'react';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common/on-stage';
import { AccordionItem } from './Item/AccordionItem';
import { classes, st } from './Accordion.st.css';
import { KEYS } from '../../common/keyCodes';
import {
  AccordionDefaultProps,
  AccordionFocusElement,
  AccordionFocusItem,
  AccordionProps,
  AccordionSize,
  AccordionState,
  AccordionTheme,
} from './types';

const expandAnimationDuration = 300;

/** A component showing a list of expandable/collapsible items */
export class Accordion extends React.Component<AccordionProps, AccordionState> {
  static displayName = 'Accordion';
  static defaultProps: AccordionDefaultProps = {
    icons: {
      expanded: ChevronUp,
      collapsed: ChevronDown,
    },
    multiple: false,
    size: AccordionSize.Small,
    theme: AccordionTheme.Line,
  };
  static Item: typeof AccordionItem = AccordionItem;

  state: AccordionState = {
    items: [],
    itemRefs: [],
    focusedItem: null,
  };

  rootRef = React.createRef<HTMLDivElement>();

  componentDidMount = () => {
    this.initItems();
  };

  componentDidUpdate = (prevProps: Readonly<AccordionProps>) => {
    if (!prevProps) {
      return;
    }

    const currentChildren = React.Children.toArray(
      this.props.children,
    ) as React.ReactElement[];

    const hasChildrenChanged = React.Children.map(
      prevProps.children,
      (child) => child.props,
    )?.some(
      (props, index) =>
        currentChildren[index]?.props &&
        currentChildren[index].props.id !== props.id,
    );

    if (hasChildrenChanged) {
      this.initItems();
    }
  };

  private readonly handleArrowDown = (): AccordionFocusItem | null => {
    const { focusedItem, itemRefs } = this.state;

    if (!focusedItem || focusedItem.element !== AccordionFocusElement.Header) {
      return null;
    }

    return {
      index: (focusedItem.index + 1) % itemRefs.length,
      element: AccordionFocusElement.Header,
    };
  };

  private readonly handleArrowUp = (): AccordionFocusItem | null => {
    const { focusedItem, itemRefs } = this.state;

    if (!focusedItem || focusedItem.element !== AccordionFocusElement.Header) {
      return null;
    }

    const previousItemIndex =
      (((focusedItem.index - 1) % itemRefs.length) + itemRefs.length) %
      itemRefs.length;

    return {
      index: previousItemIndex,
      element: AccordionFocusElement.Header,
    };
  };

  private readonly focusItem = (focusItem: AccordionFocusItem | null) => {
    if (!focusItem) {
      return;
    }

    if (focusItem.element === AccordionFocusElement.Content) {
      this.state.itemRefs[focusItem.index].contentRef?.current?.focus();
    } else {
      this.state.itemRefs[focusItem.index].headerRef?.current?.focus();
    }

    this.setState({
      focusedItem: focusItem,
    });
  };

  private readonly handledEvents = [
    KEYS.ArrowUp,
    KEYS.ArrowDown,
    KEYS.Home,
    KEYS.End,
    KEYS.Spacebar,
    KEYS.Enter,
  ];

  private readonly _isEventOnAccordionItemContent = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    const { focusedItem, itemRefs } = this.state;
    const focusedItemRefs = itemRefs[focusedItem.index];
    const isInnerContent = focusedItemRefs?.contentRef?.current.contains(
      event.target as Node | null,
    );
    const isAccordionItem =
      event.target === focusedItemRefs?.contentRef?.current;
    return isInnerContent && !isAccordionItem;
  };

  private readonly onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const isInnerContent = this._isEventOnAccordionItemContent(event);
    if (isInnerContent) {
      return;
    }

    if (this.handledEvents.includes(event.key as KEYS)) {
      event.preventDefault();
    }
  };

  private readonly onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { multiple } = this.props;
    const { focusedItem, items } = this.state;

    const isInnerContent = this._isEventOnAccordionItemContent(event);
    if (isInnerContent) {
      return;
    }

    if (this.handledEvents.includes(event.key as KEYS)) {
      event.preventDefault();
    }

    if (event.key === KEYS.ArrowDown) {
      this.focusItem(this.handleArrowDown());
    }

    if (event.key === KEYS.ArrowUp) {
      this.focusItem(this.handleArrowUp());
    }

    if (event.key === KEYS.Home) {
      this.focusItem({
        index: 0,
        element: AccordionFocusElement.Header,
      });
    }

    if (event.key === KEYS.End) {
      this.focusItem({
        index: this.state.itemRefs.length - 1,
        element: AccordionFocusElement.Header,
      });
    }

    if (event.key === KEYS.Escape) {
      if (multiple) {
        this.closeAllItems();
      } else if (items[focusedItem.index].isExpanded) {
        this.closeItem(items[focusedItem.index].id);
      }
    }

    if (event.key === KEYS.Spacebar || event.key === KEYS.Enter) {
      this.handleItemClick(items[focusedItem.index].id);
    }
  };

  private readonly onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    const { itemRefs } = this.state;

    const focusedHeaderIdx = itemRefs.findIndex(
      ({ headerRef }) => headerRef?.current === (event.target as Node),
    );

    const focusedContentIdx = itemRefs.findIndex(({ contentRef }) => {
      const isItemFocused = contentRef?.current === (event.target as Node);
      const isItemContentFocused = contentRef?.current?.contains(
        event.target as Node,
      );
      return isItemFocused || isItemContentFocused;
    });

    if (focusedHeaderIdx !== -1) {
      this.setState({
        focusedItem: {
          index: focusedHeaderIdx,
          element: AccordionFocusElement.Header,
        },
      });
    } else if (focusedContentIdx !== -1) {
      this.setState({
        focusedItem: {
          index: focusedContentIdx,
          element: AccordionFocusElement.Content,
        },
      });
    }
  };

  private readonly onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const contains = this.rootRef?.current?.contains(
      event.relatedTarget as Node | null,
    );
    if (!contains) {
      this.setState({
        focusedItem: null,
      });
    }
  };

  private readonly handleItemClick = (id: string) => {
    const { onItemClick } = this.props;
    const { items } = this.state;
    const clickedItem = items.find((item) => item.id === id);

    onItemClick?.(id, !clickedItem?.isExpanded);

    if (clickedItem?.isExpanded) {
      this.closeItem(id);
    } else {
      this.expandItem(id);
    }
  };

  private readonly expandItem = (id: string) => {
    const { items } = this.state;
    this.setItemExpanded(id, true);
    const expandedItemIndex = items.findIndex((item) => item.id === id);

    setTimeout(
      () =>
        this.focusItem({
          index: expandedItemIndex,
          element: AccordionFocusElement.Content,
        }),
      expandAnimationDuration,
    );
  };

  private readonly closeItem = (id: string) => {
    const { items } = this.state;
    this.setItemExpanded(id, false);

    const closedItemIndex = items.findIndex((item) => item.id === id);
    this.focusItem({
      index: closedItemIndex,
      element: AccordionFocusElement.Header,
    });
  };

  private readonly setItemExpanded = (id: string, itemExpanded: boolean) => {
    const { items } = this.state;
    const { multiple } = this.props;
    const toggledItems = items.map((item) => ({
      id: item.id,
      isExpanded:
        item.id === id ? itemExpanded : multiple ? item.isExpanded : false,
    }));

    this.setState({
      items: toggledItems,
    });
  };

  private readonly closeAllItems = () => {
    const { items, focusedItem } = this.state;
    const toggledItems = items.map((item) => ({
      id: item.id,
      isExpanded: false,
    }));

    this.focusItem({
      index: focusedItem.index,
      element: AccordionFocusElement.Header,
    });

    this.setState({
      items: toggledItems,
    });
  };

  private readonly initItems = () => {
    const { initiallyExpanded, children } = this.props;

    const items = React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        return null;
      }

      const isInitiallyExpanded =
        typeof initiallyExpanded === 'string'
          ? initiallyExpanded === child.props.id
          : initiallyExpanded?.some((id) => id === child.props.id);

      return {
        id: child.props.id,
        isExpanded: isInitiallyExpanded,
      };
    })?.filter((item) => item);

    this.setState({
      items,
      itemRefs: items?.map(() => ({
        headerRef: React.createRef(),
        contentRef: React.createRef(),
      })),
    });
  };

  render = () => {
    const {
      className,
      children,
      size,
      'aria-label': ariaLabel,
      theme,
      icons,
    } = this.props;
    const { items, itemRefs } = this.state;

    return (
      <div
        className={st(classes.root, {}, className)}
        data-hook={this.props['data-hook']}
        role="region"
        aria-label={ariaLabel}
        onKeyDown={this.onKeyDown}
        onKeyUp={this.onKeyUp}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        ref={this.rootRef}
      >
        {React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) {
            return child;
          }
          const isExpanded = !!items.find((item) => item.id === child.props.id)
            ?.isExpanded;

          return React.cloneElement<any>(child, {
            icons,
            isExpanded,
            size,
            theme,
            onClick: () => this.handleItemClick(child.props.id),
            className: classes.itemContent,
            ...itemRefs[index],
          });
        })}
      </div>
    );
  };
}
